import React from "react";
import { Box, Typography, Button, BrokenImageIcon, ImageCarousel, CheckIcon } from "../../ui-kit";
import RejectionPanel from "../RejectionPanel";
import RejectedCard from "../RejectedCard";
import { DocumentItem, DocFieldState } from "../types";
import { RejectionReason } from "../../redux/onBoardingSlice.types";
import { useStyles } from "./DocumentField.styles";
import { useDocFieldState } from "./useDocFieldState";
import { ONBOARDING_TEXT } from "../text.constants";

export interface DocumentFieldProps {
  doc: DocumentItem;
  rejectionReasons?: RejectionReason[];
  /** Controlled mode: pass external state + handlers from a parent. */
  docState?: DocFieldState;
  onView?: () => void;
  onStartRejection?: () => void;
  onClosePreview?: () => void;
  onSelectReason?: (r: string) => void;
  onCommentChange?: (v: string) => void;
  onClearAll?: () => void;
  onCancelRejection?: () => void;
  onConfirmRejection?: () => void;
  className?: string;
}

/** Pure renderer — all state logic lives in useDocFieldState. */
const DocumentField = ({
  doc,
  rejectionReasons = [],
  docState: externalState,
  onView: extOnView,
  onStartRejection: extOnStartRejection,
  onSelectReason: extOnSelect,
  onCommentChange: extOnCommentChange,
  onClearAll: extOnClear,
  onCancelRejection: extOnCancel,
  onConfirmRejection: extOnConfirm,
  className,
}: DocumentFieldProps) => {
  const classes = useStyles();
  const {
    state,
    isOpen,
    rejectPanelRef,
    handleView,
    handleStartRejection,
    handleSelect,
    handleCancel,
    handleConfirm,
  } = useDocFieldState({
    externalState,
    onView: extOnView,
    onStartRejection: extOnStartRejection,
    onSelectReason: extOnSelect,
    onCommentChange: extOnCommentChange,
    onClearAll: extOnClear,
    onCancelRejection: extOnCancel,
    onConfirmRejection: extOnConfirm,
    rejectionReasons,
  });

  const images = doc.previewUrls ?? [];

  if (state.state === "rejected") {
    return (
      <RejectedCard
        label={doc.label}
        fileName={doc.fileName}
        thumbnailUrl={doc.thumbnailUrl}
        reasons={state.reasons}
        className={className}
      />
    );
  }

  return (
    <Box className={`${classes.root}${className ? ` ${className}` : ""}`}>
      <Typography className={classes.label}>{doc.label}</Typography>

      <Box className={classes.docRow}>
        <Box className={classes.thumbnailWrapper}>
          <Box className={classes.thumbnail} />
          {state.viewed && (
            <Box className={classes.viewedBadge}>
              <CheckIcon />
            </Box>
          )}
        </Box>
        <Typography className={classes.docName}>
          {doc.fileName}
          {images.length > 1 && (
            <Typography className={classes.imageCount}>({images.length} images)</Typography>
          )}
        </Typography>
        <Box className={classes.actions}>
          <Button
            variant="outlined"
            size="small"
            className={isOpen ? classes.viewBtnOpen : classes.viewBtn}
            onClick={handleView}
            disableElevation
          >
            {isOpen ? ONBOARDING_TEXT.DOC_CLOSE_BTN : ONBOARDING_TEXT.DOC_VIEW_BTN}
          </Button>
        </Box>
      </Box>

      {isOpen && (
        <>
          {images.length > 0 ? (
            <ImageCarousel images={images} />
          ) : (
            <Box className={classes.noImageWrap}>
              <BrokenImageIcon className={classes.noImageIcon} />
            </Box>
          )}
        </>
      )}

      {state.state === "rejecting" && (
        <div ref={rejectPanelRef}>
          <RejectionPanel
            reasons={rejectionReasons}
            selectedReason={state.pendingReason}
            onSelect={handleSelect}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />
        </div>
      )}
    </Box>
  );
};

export default DocumentField;

